import { useState } from 'react';
import { ChevronDown, ChevronRight, Copy, Check, FileCode, Eye, Edit3, Plus, Trash2, Loader2 } from 'lucide-react';
import { cn } from '@/utils/cn';

export type FileAction = 'reading' | 'editing' | 'creating' | 'deleting';

interface CodeBlockWithHeaderProps {
  action: FileAction;
  filePath: string;
  language?: string;
  content: string;
  isLoading?: boolean;
  defaultExpanded?: boolean;
  className?: string;
}

const actionConfig: Record<FileAction, {
  label: string;
  icon: typeof Eye;
  bgColor: string;
  textColor: string;
  borderColor: string;
}> = {
  reading: {
    label: 'Reading',
    icon: Eye,
    bgColor: 'bg-blue-50 dark:bg-blue-900/20',
    textColor: 'text-blue-600 dark:text-blue-400',
    borderColor: 'border-blue-200 dark:border-blue-800'
  },
  editing: {
    label: 'Editing',
    icon: Edit3,
    bgColor: 'bg-amber-50 dark:bg-amber-900/20',
    textColor: 'text-amber-600 dark:text-amber-400',
    borderColor: 'border-amber-200 dark:border-amber-800'
  },
  creating: {
    label: 'Creating',
    icon: Plus,
    bgColor: 'bg-green-50 dark:bg-green-900/20',
    textColor: 'text-green-600 dark:text-green-400',
    borderColor: 'border-green-200 dark:border-green-800'
  },
  deleting: {
    label: 'Deleting',
    icon: Trash2,
    bgColor: 'bg-red-50 dark:bg-red-900/20',
    textColor: 'text-red-600 dark:text-red-400',
    borderColor: 'border-red-200 dark:border-red-800'
  }
};

export function CodeBlockWithHeader({
  action,
  filePath,
  language,
  content,
  isLoading = false,
  defaultExpanded = false,
  className
}: CodeBlockWithHeaderProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [copied, setCopied] = useState(false);

  const config = actionConfig[action];
  const Icon = config.icon;

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const fileName = filePath.split('/').pop() || filePath;

  return (
    <div className={cn(
      'rounded-lg border overflow-hidden',
      config.borderColor,
      className
    )}>
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={cn(
          'w-full flex items-center gap-2 px-3 py-2',
          config.bgColor,
          'hover:opacity-90 transition-opacity'
        )}
      >
        {isExpanded ? (
          <ChevronDown className={cn('w-4 h-4', config.textColor)} />
        ) : (
          <ChevronRight className={cn('w-4 h-4', config.textColor)} />
        )}

        {isLoading ? (
          <Loader2 className={cn('w-4 h-4 animate-spin', config.textColor)} />
        ) : (
          <Icon className={cn('w-4 h-4', config.textColor)} />
        )}

        <span className={cn('text-sm font-medium', config.textColor)}>
          {config.label}
        </span>

        <FileCode className="w-4 h-4 text-gray-400 ml-1" />
        <span className="text-sm text-gray-600 dark:text-gray-400 font-mono truncate flex-1 text-left">
          {filePath}
        </span>

        {language && (
          <span className="text-xs px-2 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
            {language}
          </span>
        )}
      </button>

      {/* Code content */}
      {isExpanded && (
        <div className="relative">
          <button
            onClick={handleCopy}
            className="absolute top-2 right-2 p-1.5 rounded bg-gray-700 hover:bg-gray-600 transition-colors z-10"
            title="Copy code"
          >
            {copied ? (
              <Check className="w-4 h-4 text-green-400" />
            ) : (
              <Copy className="w-4 h-4 text-gray-300" />
            )}
          </button>

          <pre className="p-4 bg-gray-900 overflow-x-auto">
            <code className={cn(
              'text-sm font-mono text-gray-100',
              language && `language-${language}`
            )}>
              {content}
            </code>
          </pre>
        </div>
      )}
    </div>
  );
}

// Compact version for file operation status display
interface FileOperationCardProps {
  action: FileAction;
  filePath: string;
  isLoading?: boolean;
  success?: boolean;
  className?: string;
}

export function FileOperationCard({
  action,
  filePath,
  isLoading = false,
  success,
  className
}: FileOperationCardProps) {
  const config = actionConfig[action];
  const Icon = config.icon;

  return (
    <div className={cn(
      'flex items-center gap-2 px-3 py-2 rounded-lg border',
      config.bgColor,
      config.borderColor,
      className
    )}>
      {isLoading ? (
        <Loader2 className={cn('w-4 h-4 animate-spin', config.textColor)} />
      ) : success !== undefined ? (
        success ? (
          <Check className="w-4 h-4 text-green-500" />
        ) : (
          <span className="w-4 h-4 text-red-500">!</span>
        )
      ) : (
        <Icon className={cn('w-4 h-4', config.textColor)} />
      )}

      <span className={cn('text-sm font-medium', config.textColor)}>
        {config.label}
      </span>

      <span className="text-sm text-gray-600 dark:text-gray-400 font-mono truncate">
        {filePath}
      </span>
    </div>
  );
}
